import { inject, Injectable, ViewContainerRef } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, of, switchMap, take } from 'rxjs';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { FullCandidateSelectors } from '@core/candidate-data';
import { I18nService } from '@core/i18n-legacy';
import { PlacementSelectors } from '@core/placements-data';
import type { Placement } from '@core/placements-types';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

@Injectable({ providedIn: 'root' })
export class AccessToCandidateService {
  private readonly i18n = inject(I18nService);

  private readonly dialogService = inject(DialogService);

  private readonly store = inject(Store);

  private abilities = this.store.selectSignal(AbilitiesSelectors.abilityMap);

  /**
   * Check if the current user could access the candidate without the placement
   */
  public checkCouldAccessCandidateWithoutPlacement$(
    placementId: number,
    candidateId: number,
  ): Observable<boolean> {
    if (!this.abilities()[Ability.ViewCandidates]) {
      return of(false);
    }

    return combineLatest([
      this.store.select(FullCandidateSelectors.currentUserAvailablePlacements({ candidateId })),
      this.store.select(FullCandidateSelectors.byIdWithReferences(candidateId)),
    ]).pipe(
      take(1),
      map(([availablePlacements, candidate]) => {
        if (!candidate) {
          return false;
        }

        if (this.abilities()[Ability.ViewAllJobs]) {
          return true;
        }

        const remainingPlacements = availablePlacements.filter(
          availablePlacement => availablePlacement.id !== placementId,
        );

        if (!remainingPlacements.length) {
          return false;
        }

        const isCandidateHired = candidate.placements.some(
          placement => placement.hiredInThisPlacement,
        );

        if (isCandidateHired) {
          const willBeHiredAfterUnassign = remainingPlacements.some(
            placement => placement.hiredInThisPlacement,
          );
          return this.abilities()[Ability.ViewHiredCandidates] || !willBeHiredAfterUnassign;
        }

        return true;
      }),
    );
  }

  public promptAccessLossConfirmation$(
    placementIds: number[],
    vcRef?: ViewContainerRef,
  ): Observable<boolean> {
    const toOfferList = (placements: Placement[]) =>
      placements
        .map(
          placement =>
            `"${placement.offer ? placement.offer!.title : placement.talentPool!.title}"`,
        )
        .join(', ');

    return this.store.select(PlacementSelectors.byIdsWithReferences(placementIds)).pipe(
      take(1),
      switchMap(placements =>
        this.dialogService.error(
          {
            title: this.i18n.translate('app.candidate.you_will_lose_access'),
            message: this.i18n.translate('app.candidate.you_lose_access_msg', {
              offerList: toOfferList(placements),
            }),
            agreeLabel: this.i18n.translate('app.common.remove'),
            cancelLabel: this.i18n.translate('app.common.cancel'),
            width: DialogWidth.SmallXX,
            confirmActionsLayout: 'space-between',
          },
          vcRef,
        ),
      ),
    );
  }
}
